/**
 * WinDealCustomerModal - Modal para cadastro de cliente ao ganhar negócio
 * 
 * Exibido quando o contato do deal não tem cliente vinculado.
 * Campos obrigatórios:
 * - PF: Nome Completo
 * - PJ: Razão Social
 * Demais campos (CPF, CNPJ, endereço) são opcionais.
 * 
 * Chama RPC atômica mark_deal_as_won_with_customer para garantir transação única.
 * 
 * @version 1.0.0
 */
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  User, 
  Building2, 
  MapPin, 
  Search,
  Loader2,
  Trophy,
  Calendar,
  AlertCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { applyCPFMask, applyCNPJMask, applyDateMask } from '@/lib/input-masks';
import { validateCPF, validateCNPJ, validateCEP } from '@/lib/input-validators';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('WinDealCustomerModal');

// ============================================
// FUNÇÕES DE CONVERSÃO DE DATA
// ============================================

/**
 * Converte data do formato brasileiro (DD/MM/YYYY) para ISO (YYYY-MM-DD)
 * @param date - Data no formato DD/MM/YYYY
 * @returns Data no formato YYYY-MM-DD ou null se inválida
 */
function convertBrazilianDateToISO(date: string | null | undefined): string | null {
  if (!date) return null;
  
  const cleanDate = date.replace(/\D/g, '');
  if (cleanDate.length !== 8) return null;
  
  const day = cleanDate.slice(0, 2);
  const month = cleanDate.slice(2, 4);
  const year = cleanDate.slice(4, 8);
  
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);
  
  if (dayNum < 1 || dayNum > 31) return null;
  if (monthNum < 1 || monthNum > 12) return null;
  if (yearNum < 1900 || yearNum > 2100) return null;
  
  return `${year}-${month}-${day}`;
}

// ============================================
// SCHEMA DE VALIDAÇÃO
// ============================================
const winDealCustomerSchema = z.object({
  customer_type: z.enum(['pf', 'pj']),
  
  // Campos PF
  nome: z.string().optional().nullable(),
  cpf: z.string().optional().nullable(),
  data_nascimento: z.string().optional().nullable(),
  
  // Campos PJ
  razao_social: z.string().optional().nullable(),
  nome_fantasia: z.string().optional().nullable(),
  cnpj: z.string().optional().nullable(),
  
  // Endereço - Opcionais
  cep: z.string().optional().nullable(),
  logradouro: z.string().optional().nullable(),
  numero: z.string().optional().nullable(),
  complemento: z.string().optional().nullable(),
  bairro: z.string().optional().nullable(),
  cidade: z.string().optional().nullable(),
  estado: z.string().optional().nullable(),
}).superRefine((data, ctx) => {
  // Validações condicionais por tipo de cliente
  if (data.customer_type === 'pf') {
    if (!data.nome || data.nome.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nome completo é obrigatório para Pessoa Física',
        path: ['nome'],
      });
    }
    // CPF obrigatório para PF
    if (!data.cpf || data.cpf.replace(/\D/g, '').length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CPF é obrigatório para Pessoa Física',
        path: ['cpf'],
      });
    }
  } else {
    if (!data.razao_social || data.razao_social.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Razão Social é obrigatória para Pessoa Jurídica',
        path: ['razao_social'],
      });
    }
    // CNPJ obrigatório para PJ
    if (!data.cnpj || data.cnpj.replace(/\D/g, '').length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CNPJ é obrigatório para Pessoa Jurídica',
        path: ['cnpj'],
      });
    }
  }
});

type WinDealCustomerFormValues = z.infer<typeof winDealCustomerSchema>;

// ============================================
// PROPS
// ============================================
interface WinDealCustomerModalProps {
  /** ID do deal a ser marcado como ganho */
  dealId: string;
  /** Nome do contato associado ao deal (para exibição) */
  contactName?: string | null;
  /** Título do deal (para exibição) */
  dealTitle?: string;
  /** Estado de abertura do dialog */
  open: boolean;
  /** Callback para alteração do estado */
  onOpenChange: (open: boolean) => void;
  /** Callback de sucesso após ganhar o negócio */
  onSuccess?: () => void;
}

// ============================================
// COMPONENTE
// ============================================
export function WinDealCustomerModal({
  dealId,
  contactName,
  dealTitle,
  open,
  onOpenChange,
  onSuccess,
}: WinDealCustomerModalProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // Formulário com react-hook-form
  const form = useForm<WinDealCustomerFormValues>({
    resolver: zodResolver(winDealCustomerSchema),
    defaultValues: {
      customer_type: 'pf',
      cep: null,
      logradouro: null,
      numero: null,
      complemento: null,
      bairro: null,
      cidade: null,
      estado: null,
    },
  });

  // Observa tipo de cliente para renderização condicional
  const customerType = form.watch('customer_type');

  // Reset form quando abrir modal
  useEffect(() => {
    if (open) {
      form.reset({
        customer_type: 'pf',
        nome: contactName || '',
        cpf: null,
        data_nascimento: null,
        razao_social: null,
        nome_fantasia: null,
        cnpj: null,
        cep: null,
        logradouro: null,
        numero: null,
        complemento: null,
        bairro: null,
        cidade: null,
        estado: null,
      });
    }
  }, [open, contactName, form]);

  // ============================================
  // BUSCA CEP VIA VIACEP
  // ============================================
  const handleCepSearch = async () => {
    const cep = form.getValues('cep')?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      toast.error('CEP inválido. Digite 8 dígitos.');
      return;
    }

    setIsLoadingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado.');
        return;
      }
      
      // Preenche campos de endereço
      form.setValue('logradouro', data.logradouro || '');
      form.setValue('bairro', data.bairro || '');
      form.setValue('cidade', data.localidade || '');
      form.setValue('estado', data.uf || '');
      
      // Foca no campo número
      const numeroInput = document.querySelector('input[name="numero"]');
      if (numeroInput) {
        (numeroInput as HTMLInputElement).focus();
      }
      
      toast.success('Endereço preenchido!');
    } catch (error) {
      logger.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setIsLoadingCep(false);
    }
  };

  // ============================================
  // SUBMIT - Chama RPC Atômica
  // ============================================
  const onSubmit = async (values: WinDealCustomerFormValues) => {
    // Validação adicional de CPF/CNPJ
    if (values.customer_type === 'pf' && values.cpf) {
      const cpfResult = validateCPF(values.cpf);
      if (!cpfResult.isValid) {
        form.setError('cpf', { message: cpfResult.error });
        return;
      }
    }
    
    if (values.customer_type === 'pj' && values.cnpj) {
      const cnpjResult = validateCNPJ(values.cnpj);
      if (!cnpjResult.isValid) {
        form.setError('cnpj', { message: cnpjResult.error });
        return;
      }
    }

    // Validação de CEP - apenas se preenchido
    if (values.cep && values.cep.replace(/\D/g, '').length > 0) {
      const cepResult = validateCEP(values.cep);
      if (!cepResult.isValid) {
        form.setError('cep', { message: cepResult.error });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      logger.info('Chamando RPC mark_deal_as_won_with_customer', { dealId });

      // Chama RPC atômica
      const { data, error } = await supabase.rpc('mark_deal_as_won_with_customer', {
        p_deal_id: dealId,
        p_customer_type: values.customer_type,
        p_nome: values.nome || null,
        p_cpf: values.cpf || null,
        p_data_nascimento: convertBrazilianDateToISO(values.data_nascimento),
        p_razao_social: values.razao_social || null,
        p_nome_fantasia: values.nome_fantasia || null,
        p_cnpj: values.cnpj || null,
        p_cep: values.cep || null,
        p_logradouro: values.logradouro || null,
        p_numero: values.numero || null,
        p_complemento: values.complemento || null,
        p_bairro: values.bairro || null,
        p_cidade: values.cidade || null,
        p_estado: values.estado || null,
      });

      if (error) {
        logger.error('Erro RPC mark_deal_as_won_with_customer:', error);
        throw error;
      }

      logger.info('RPC executada com sucesso:', data);

      // Invalidar caches para atualização da UI
      const result = data as {
        success: boolean;
        deal_id: string;
        customer_id: string;
        customer_created: boolean;
        pipeline_id: string;
        previous_phase_id: string;
        new_phase_id: string;
      };

      // Invalidar caches
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
      queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      
      // Invalidar colunas do Kanban
      if (result.pipeline_id) {
        if (result.previous_phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', result.pipeline_id, result.previous_phase_id] 
          });
        }
        if (result.new_phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', result.pipeline_id, result.new_phase_id] 
          });
        }
        queryClient.invalidateQueries({ 
          queryKey: ['pipeline-phase-stats', result.pipeline_id] 
        });
      }

      // Feedback de sucesso
      if (result.customer_created) {
        toast.success('🎉 Negócio ganho e cliente cadastrado com sucesso!');
      } else {
        toast.success('🎉 Negócio marcado como ganho!');
      }

      // Fechar modal e notificar sucesso
      onOpenChange(false);
      onSuccess?.();

    } catch (error: unknown) {
      // Tratamento de erros específicos do Postgres para mensagens intuitivas ao usuário.
      // Importante: o supabase-js entrega PostgrestError como objeto plano (não Error),
      // por isso usamos `pgError?.message` em vez de `instanceof Error` para não cair no
      // fallback genérico "Erro desconhecido" e mascarar mensagens vindas do banco/triggers.
      const pgError = error as { code?: string; message?: string; hint?: string; details?: string };
      let errorMessage: string;

      // Trigger validate_deal_status_transition: pipeline sem fase Ganho/Perdido cadastrada.
      // Exibimos a mensagem do banco diretamente, sem prefixo, pois já é amigável e acionável.
      if (
        pgError?.hint === 'NO_WON_PHASE' ||
        pgError?.hint === 'NO_LOST_PHASE' ||
        pgError?.message?.includes('Não é possível marcar como Ganho') ||
        pgError?.message?.includes('Não é possível marcar como Perdido')
      ) {
        errorMessage = pgError.message as string;
      } else if (
        pgError?.code === '23505' ||
        pgError?.message?.includes('duplicate key') ||
        pgError?.message?.includes('idx_crm_customers_cpf_tenant') ||
        pgError?.message?.includes('idx_crm_customers_cnpj_tenant')
      ) {
        // Constraint de unicidade CPF/CNPJ por tenant - mensagem acionável
        errorMessage = customerType === 'pf'
          ? 'Já existe um cliente cadastrado com este CPF. Verifique na lista de clientes ou utilize outro CPF.'
          : 'Já existe um cliente cadastrado com este CNPJ. Verifique na lista de clientes ou utilize outro CNPJ.';
      } else {
        errorMessage = pgError?.message || (error instanceof Error ? error.message : 'Erro desconhecido');
      }

      logger.error('Erro ao ganhar negócio:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // MÁSCARAS DE INPUT
  // ============================================
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCPFMask(e.target.value);
    form.setValue('cpf', masked);
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCNPJMask(e.target.value);
    form.setValue('cnpj', masked);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    form.setValue('data_nascimento', masked);
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const limited = value.slice(0, 8);
    let formatted = limited;
    if (limited.length > 5) {
      formatted = `${limited.slice(0, 5)}-${limited.slice(5, 8)}`;
    }
    form.setValue('cep', formatted);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <Trophy className="h-5 w-5" />
            Ganhar Negócio
          </DialogTitle>
          <DialogDescription>
            Para finalizar a venda, preencha os dados do cliente abaixo.
            {dealTitle && (
              <span className="block mt-1 font-medium text-foreground">
                Negócio: {dealTitle}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Alerta informativo */}
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-700 dark:text-amber-400">
            O contato deste negócio ainda não possui cliente vinculado. 
            Preencha os dados obrigatórios para criar o cadastro do cliente.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Tipo de Cliente */}
            <FormField
              control={form.control}
              name="customer_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Cliente *</FormLabel>
                  <Tabs
                    value={field.value}
                    onValueChange={field.onChange}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="pf" className="gap-2">
                        <User className="h-4 w-4" />
                        Pessoa Física
                      </TabsTrigger>
                      <TabsTrigger value="pj" className="gap-2">
                        <Building2 className="h-4 w-4" />
                        Pessoa Jurídica
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormItem>
              )}
            />

            <Separator />

            {/* Campos Pessoa Física */}
            {customerType === 'pf' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Dados Pessoais
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="nome"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Nome Completo *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nome completo do cliente" 
                            {...field} 
                            value={field.value || ''} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cpf"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CPF *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="000.000.000-00" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCpfChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="data_nascimento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Nascimento</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              placeholder="DD/MM/AAAA" 
                              {...field}
                              value={field.value || ''}
                              onChange={handleDateChange}
                            />
                            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Campos Pessoa Jurídica */}
            {customerType === 'pj' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Dados da Empresa
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="razao_social"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Razão Social *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Razão social da empresa" 
                            {...field} 
                            value={field.value || ''} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="nome_fantasia"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Fantasia</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nome fantasia" 
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cnpj"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CNPJ *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="00.000.000/0000-00" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCnpjChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            <Separator />

            {/* Endereço */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Endereço
              </h4>
              <div className="grid gap-4 sm:grid-cols-6">
                <FormField
                  control={form.control}
                  name="cep"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input 
                            placeholder="00000-000" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCepChange}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleCepSearch}
                            disabled={isLoadingCep}
                          >
                            {isLoadingCep ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="logradouro"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-4">
                      <FormLabel>Logradouro</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Rua, Avenida..." 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numero"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-1">
                      <FormLabel>Número</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Nº" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="complemento"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Complemento</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Apto, Sala..." 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bairro"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-3">
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Bairro" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cidade"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-4">
                      <FormLabel>Cidade</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Cidade" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="estado"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>UF</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="UF" 
                          maxLength={2}
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Trophy className="h-4 w-4 mr-2" />
                    Ganhar Negócio
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
